import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { BookOpen, GraduationCap, CheckCircle, XCircle, LogOut, User, Loader2, FileText, Award, Clock, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, logout } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [allSchools, setAllSchools] = useState([]);
  const [schoolExams, setSchoolExams] = useState({});
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Fetch all schools
      const schoolsRes = await fetch(`${BACKEND_URL}/api/schools`);
      const schoolsData = await schoolsRes.json();
      setAllSchools(schoolsData);

      // Fetch user enrollments
      const enrollRes = await fetch(`${BACKEND_URL}/api/schools/my-enrollments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const enrollData = await enrollRes.json();
      setEnrollments(enrollData);

      // Fetch exam info for each enrolled school
      const examsInfo = {};
      for (const enrollment of enrollData) {
        try {
          const examRes = await fetch(`${BACKEND_URL}/api/schools/${enrollment.id}/exams`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (examRes.ok) {
            examsInfo[enrollment.id] = await examRes.json();
          }
        } catch (e) {
          console.error(`Error fetching exams for ${enrollment.id}:`, e);
        }
      }
      setSchoolExams(examsInfo);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token, authLoading, navigate, fetchData]);

  const handleEnroll = async (schoolId) => {
    setEnrolling(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ school_ids: [schoolId] })
      });

      if (!response.ok) throw new Error('Enrollment failed');

      toast.success('Enrolled successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEnrolling(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const enrolledSchoolIds = enrollments.map(e => e.id);
  const availableSchools = allSchools.filter(s => !enrolledSchoolIds.includes(s.id));

  // Show loading while auth context is loading or data is loading
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5f7c8a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* User Info Header */}
          <div className="bg-[#5f7c8a] rounded-lg p-6 mb-8 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-full">
                  <User size={32} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">{user?.name}</h1>
                  <p className="text-white/80">{user?.email}</p>
                  <p className="text-white/60 text-sm">Branch: {user?.branch}</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={handleLogout}
              >
                <LogOut size={16} className="mr-2" /> Logout
              </Button>
            </div>
          </div>

          {/* My Enrollments */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <GraduationCap className="text-[#5f7c8a]" /> My Enrolled Schools
            </h2>
            
            {enrollments.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <BookOpen size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">You have not enrolled in any schools yet.</p>
                  <p className="text-gray-400 text-sm">Browse available schools below to get started!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {enrollments.map((school) => {
                  const examInfo = schoolExams[school.id];
                  return (
                    <Card key={school.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">{school.name}</CardTitle>
                        <CardDescription>Enrolled School</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Mock Exam Section */}
                        <div className="border rounded-lg p-4 bg-blue-50/50">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <FileText size={18} className="text-blue-600" />
                              <span className="font-medium text-gray-800">Mock Exam</span>
                            </div>
                            <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                              Practice
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                            <span className="flex items-center gap-1">
                              <Clock size={14} /> {examInfo?.mock_exam?.duration_minutes || 12} min
                            </span>
                            <span>{examInfo?.mock_exam?.question_count || school.question_count || 5} Questions</span>
                            <span>{examInfo?.mock_exam?.passing_score || 60}% to pass</span>
                          </div>

                          {school.exam_taken ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                {school.exam_passed ? (
                                  <Badge className="bg-green-500"><CheckCircle size={14} className="mr-1" /> Passed</Badge>
                                ) : (
                                  <Badge variant="destructive"><XCircle size={14} className="mr-1" /> Not Passed</Badge>
                                )}
                                <span className="text-gray-600 text-sm">Score: {school.exam_score?.toFixed(0)}%</span>
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="w-full"
                                onClick={() => navigate(`/exam/${school.id}`)}
                              >
                                Retake Mock Exam
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              size="sm"
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              onClick={() => navigate(`/exam/${school.id}`)}
                            >
                              Take Mock Exam
                            </Button>
                          )}
                        </div>

                        {/* Final Exam Section */}
                        <div className={`border rounded-lg p-4 ${examInfo?.final_exam?.is_available ? 'bg-green-50/50' : 'bg-gray-50'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Award size={18} className={examInfo?.final_exam?.is_available ? 'text-green-600' : 'text-gray-400'} />
                              <span className="font-medium text-gray-800">Final Exam</span>
                              {examInfo?.final_exam?.is_password_protected && (
                                <Lock size={14} className="text-amber-500" title="Password Protected" />
                              )}
                            </div>
                            {examInfo?.final_exam?.is_available ? (
                              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                                {examInfo?.final_exam?.is_password_protected ? 'Protected' : 'Available'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-200">
                                <Lock size={12} className="mr-1" /> Coming Soon
                              </Badge>
                            )}
                          </div>

                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                            <span className="flex items-center gap-1">
                              <Clock size={14} /> {examInfo?.final_exam?.duration_minutes || 60} min
                            </span>
                            <span>{examInfo?.final_exam?.question_count || 0} Questions</span>
                          </div>

                          {examInfo?.final_exam?.is_available ? (
                            examInfo?.final_exam?.result?.taken ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  {examInfo?.final_exam?.result?.passed ? (
                                    <Badge className="bg-green-500"><CheckCircle size={14} className="mr-1" /> Passed</Badge>
                                  ) : (
                                    <Badge variant="destructive"><XCircle size={14} className="mr-1" /> Not Passed</Badge>
                                  )}
                                  <span className="text-gray-600 text-sm">
                                    Score: {examInfo?.final_exam?.result?.score?.toFixed(0)}%
                                  </span>
                                </div>
                                
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="w-full"
                                  onClick={() => navigate(`/exam/${school.id}/final`)}
                                >
                                  Retake Final Exam
                                </Button>
                              </div>
                            ) : (
                              <Button 
                                size="sm"
                                className="w-full bg-green-600 hover:bg-green-700"
                                onClick={() => navigate(`/exam/${school.id}/final`)}
                              >
                                Take Final Exam
                              </Button>
                            )
                          ) : (
                            <div className="text-center py-2">
                              <p className="text-sm text-gray-500">
                                Questions will be uploaded soon
                              </p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>

          {/* Available Schools */}
          {availableSchools.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <BookOpen className="text-[#5f7c8a]" /> Available Schools
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableSchools.map((school) => (
                  <Card key={school.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">{school.name}</CardTitle>
                      <CardDescription>{school.exam_description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <FileText size={14} className="text-blue-500" />
                          <span>Mock Exam</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Award size={14} className="text-green-500" />
                          <span>Final Exam</span>
                        </div>
                      </div>
                      <Button 
                        className="w-full bg-[#5f7c8a] hover:bg-[#4a6470]"
                        onClick={() => handleEnroll(school.id)}
                        disabled={enrolling}
                      >
                        {enrolling ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enroll Now'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DashboardPage;
